import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, Check, Sparkles, RotateCcw, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_OPTIONS = [
  { value: "1", label: "Just one site" },
  { value: "2-5", label: "2 – 5 sites" },
  { value: "6-20", label: "6 – 20 sites" },
  { value: "20+", label: "20+ sites" },
];

const PAIN_POINTS = [
  { key: "fire-damper", label: "Fire damper testing & compliance" },
  { key: "tr19", label: "Kitchen extract / TR19" },
  { key: "f-gas", label: "F-Gas / refrigeration" },
  { key: "iaq", label: "Indoor air quality / ventilation" },
  { key: "multi-contractor", label: "Multiple contractors / fragmented service" },
  { key: "reactive", label: "Reactive maintenance / unexpected failures" },
];

const SECTORS = [
  "Universities", "Schools", "Restaurants", "Laboratories",
  "Banks & Offices", "Hotels", "Pharmacies", "Healthcare", "Other",
];

const StepDot = ({ active, done }) => (
  <span
    className={`h-1.5 w-8 rounded-full transition-all duration-300 ${
      done ? "bg-brand" : active ? "bg-ink" : "bg-black/15"
    }`}
  />
);

const EMPTY = {
  sites: "", pain_points: [], sector: "",
  first_name: "", last_name: "", email: "", phone: "", company: "",
  website: "", newsletter_opt_in: false,
};

const HealthCheck = ({ onBook }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const totalSteps = 4;

  const togglePain = (key) => {
    setData((d) => ({
      ...d,
      pain_points: d.pain_points.includes(key)
        ? d.pain_points.filter((k) => k !== key)
        : [...d.pain_points, key],
    }));
  };

  const canAdvance = () => {
    if (step === 0) return Boolean(data.sites);
    if (step === 1) return data.pain_points.length > 0;
    if (step === 2) return Boolean(data.sector);
    if (step === 3)
      return (
        data.first_name.trim() &&
        data.last_name.trim() &&
        /\S+@\S+\.\S+/.test(data.email)
      );
    return true;
  };

  const submit = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/health-check`, data);
      setResult(res.data);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail?.[0]?.msg || err?.message || "Something went wrong");
    }
  };

  const reset = () => {
    setStep(0);
    setData(EMPTY);
    setResult(null);
    setStatus("idle");
  };

  return (
    <section
      id="health-check"
      className="py-24 md:py-32 bg-paper relative"
      data-testid="health-check-section"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4 inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> [ 04 — 60-second tool ]
            </div>
            <h2 className="font-display font-bold text-[2.5rem] md:text-[3.75rem] leading-[1.02] tracking-tightest">
              Compliance Health Check.
              <br />
              <span className="text-muted-foreground">Three questions. One personalised next-step plan.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16px] text-muted-foreground leading-relaxed">
              Tell us about your estate and the bits that keep you up at night.
              We’ll come back the same working day with a tailored recommendation —
              not a sales pitch, a plan.
            </p>
          </div>
        </div>

        <div className="bg-paper-warm border border-black/8 rounded-md overflow-hidden">
          {status === "done" && result ? (
            <ResultPanel result={result} reset={reset} data={data} onBook={onBook} />
          ) : (
            <>
              {/* Progress */}
              <div className="px-8 md:px-12 py-5 border-b border-black/5 flex items-center justify-between">
                <div className="font-mono text-[11px] tracking-overline uppercase text-muted">
                  Step {step + 1} of {totalSteps}
                </div>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <StepDot key={i} active={i === step} done={i < step} />
                  ))}
                </div>
              </div>

              <div className="p-8 md:p-14 min-h-[420px] flex flex-col">
                {/* Step 0 — sites */}
                {step === 0 && (
                  <div data-testid="hc-step-sites">
                    <h3 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-3">
                      How many sites are we looking at?
                    </h3>
                    <p className="text-[14px] text-muted-foreground mb-10">
                      A rough estimate is fine — we just want to scope it.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SITE_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          onClick={() => setData({ ...data, sites: o.value })}
                          data-testid={`hc-sites-${o.value}`}
                          className={`text-left p-5 rounded-sm border transition-all duration-200 ${
                            data.sites === o.value
                              ? "border-brand bg-white"
                              : "border-black/8 bg-white hover:border-black/25"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-display font-semibold text-[1.05rem] tracking-tight">
                              {o.label}
                            </span>
                            {data.sites === o.value && (
                              <Check className="h-4 w-4 text-brand" strokeWidth={3} />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 1 — pain points */}
                {step === 1 && (
                  <div data-testid="hc-step-pain">
                    <h3 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-3">
                      Which of these is on your mind?
                    </h3>
                    <p className="text-[14px] text-muted-foreground mb-10">
                      Select any that apply — there are no wrong answers.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {PAIN_POINTS.map((p) => {
                        const selected = data.pain_points.includes(p.key);
                        return (
                          <button
                            key={p.key}
                            onClick={() => togglePain(p.key)}
                            data-testid={`hc-pain-${p.key}`}
                            className={`text-left p-5 rounded-sm border transition-all duration-200 ${
                              selected
                                ? "border-brand bg-white"
                                : "border-black/8 bg-white hover:border-black/25"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-display font-medium text-[14px] tracking-tight">
                                {p.label}
                              </span>
                              <span
                                className={`shrink-0 h-4 w-4 rounded-sm border flex items-center justify-center transition-colors ${
                                  selected ? "bg-brand border-brand" : "border-black/20"
                                }`}
                              >
                                {selected && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 2 — sector */}
                {step === 2 && (
                  <div data-testid="hc-step-sector">
                    <h3 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-3">
                      What sector are you in?
                    </h3>
                    <p className="text-[14px] text-muted-foreground mb-10">
                      So we tailor the recommendation to environments we know well.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {SECTORS.map((s) => (
                        <button
                          key={s}
                          onClick={() => setData({ ...data, sector: s })}
                          data-testid={`hc-sector-${s.toLowerCase().replace(/\s+|&/g, "-")}`}
                          className={`px-5 py-3 rounded-sm border text-[14px] transition-all duration-200 ${
                            data.sector === s
                              ? "border-brand bg-brand text-white"
                              : "border-black/10 bg-white text-ink hover:border-black/30"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3 — contact */}
                {step === 3 && (
                  <div data-testid="hc-step-contact">
                    <h3 className="font-display font-bold text-[1.75rem] md:text-[2.25rem] tracking-tightest leading-tight mb-3">
                      Where shall we send the plan?
                    </h3>
                    <p className="text-[14px] text-muted-foreground mb-10">
                      We’ll come back the same working day. No spam — promise.
                    </p>
                    <div className="space-y-6 max-w-2xl">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FieldInline label="First name" name="first_name" required value={data.first_name} onChange={(e) => setData({ ...data, first_name: e.target.value })} />
                        <FieldInline label="Last name" name="last_name" required value={data.last_name} onChange={(e) => setData({ ...data, last_name: e.target.value })} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FieldInline label="Email" name="email" type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} />
                        <FieldInline label="Phone (optional)" name="phone" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} />
                      </div>
                      <FieldInline label="Company / Organisation (optional)" name="company" value={data.company} onChange={(e) => setData({ ...data, company: e.target.value })} />
                      <input type="text" tabIndex={-1} autoComplete="off" value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} className="hidden" aria-hidden="true" />
                      <NewsletterOptIn checked={data.newsletter_opt_in} onChange={(v) => setData({ ...data, newsletter_opt_in: v })} testId="hc-newsletter-optin" />
                    </div>
                    {status === "error" && (
                      <p className="mt-6 text-[13px] text-accent font-mono" data-testid="hc-error">{errorMsg}</p>
                    )}
                  </div>
                )}

                {/* Nav */}
                <div className="mt-auto pt-12 flex items-center justify-between">
                  <button
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                    data-testid="hc-back"
                    className="inline-flex items-center gap-2 text-[13px] font-mono tracking-wide text-muted-foreground hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>

                  {step < totalSteps - 1 ? (
                    <button
                      onClick={() => setStep(step + 1)}
                      disabled={!canAdvance()}
                      data-testid="hc-next"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Continue <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={!canAdvance() || status === "sending"}
                      data-testid="hc-submit"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {status === "sending" ? "Generating…" : "Get my plan"} <ArrowUpRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const FieldInline = ({ label, ...rest }) => (
  <label className="block">
    <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">{label}{rest.required && " *"}</div>
    <input
      {...rest}
      data-testid={`hc-field-${rest.name}`}
      className="w-full bg-transparent border-b border-black/15 py-3 text-[15px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-brand transition-colors"
    />
  </label>
);

export const NewsletterOptIn = ({ checked, onChange, testId }) => (
  <label className="flex items-start gap-2.5 cursor-pointer text-[12px] text-muted-foreground leading-snug">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="mt-0.5 h-4 w-4 accent-[#0048e3] shrink-0" />
    <span>Send me the quarterly Calmbreeze compliance reminder (F-Gas, TM44, seasonal checks). Unsubscribe any time.</span>
  </label>
);

const ResultPanel = ({ result, reset, data, onBook }) => {
  return (
    <div className="p-8 md:p-14" data-testid="hc-result">
      <div className="font-mono text-[11px] tracking-overline uppercase text-brand mb-4 inline-flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> [ Your plan · {result.complexity.band} ]
      </div>
      <h3 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest leading-tight mb-6">
        Hi {data.first_name}, here’s where we’d start.
      </h3>
      <p className="text-[16px] text-muted-foreground leading-relaxed max-w-2xl mb-10">
        {result.complexity.summary}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {result.recommendations.map((r) => (
          <Link
            key={r.key}
            to={`/services/${r.slug}`}
            data-testid={`hc-reco-${r.key}`}
            className="group bg-white border border-black/8 rounded-sm p-6 hover:-translate-y-1 hover:border-black/20 transition-all duration-300"
          >
            <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-3">
              {r.label}
            </div>
            <div className="font-display font-semibold text-[1.15rem] tracking-tight mb-2 group-hover:text-brand transition-colors flex items-center justify-between gap-2">
              {r.service}
              <ArrowUpRight className="h-4 w-4 text-ink/30 group-hover:text-brand transition-colors" />
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">{r.recommendation}</p>
          </Link>
        ))}
      </div>

      <div className="bg-ink text-white rounded-sm p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="font-mono text-[11px] tracking-overline uppercase text-white/50 mb-3">[ Next ]</div>
          <h4 className="font-display font-semibold text-[1.5rem] tracking-tight leading-tight text-white">
            Turn this into a costed plan with a free site survey.
          </h4>
          <p className="text-[13px] text-white/60 mt-2">We’ve emailed you a copy of this plan.</p>
        </div>
        <div className="flex md:justify-end gap-3 flex-wrap">
          <button
            onClick={() => { window.gtag?.("event", "book_survey_click", { location: "health_check_result" }); onBook?.(); }}
            data-testid="hc-result-book"
            className="inline-flex items-center gap-2 px-5 py-3 bg-accent text-white font-semibold text-[13px] rounded-sm hover:bg-brand transition-colors"
          >
            <CalendarCheck className="h-4 w-4" /> Book a free site survey
          </button>
          <Link
            to="/contact"
            data-testid="hc-result-contact"
            className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white font-semibold text-[13px] rounded-sm hover:border-white transition-colors"
          >
            Add more detail <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            onClick={reset}
            data-testid="hc-result-reset"
            className="inline-flex items-center gap-2 px-5 py-3 bg-transparent border border-white/20 text-white/80 font-semibold text-[13px] rounded-sm hover:border-white hover:text-white transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Run another
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;
